import React, { useEffect, useMemo, useState } from "react";
import {
  CButton,
  CCard,
  CCardBody,
  CCardGroup,
  CCol,
  CForm,
  CFormInput,
  CInputGroup,
  CInputGroupText,
  CRow,
  CSpinner,
} from "@coreui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeLowVision } from "@fortawesome/free-solid-svg-icons";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import validationHelper from "@/helpers/validationHelper";
import { useChangePassword } from "@/hooks";
import { loggedUser } from "@/store/features/userSlice";
import { dashboard, profile } from "@/libs/routes";
import TabNavigation from "@/components/TabNavigation";
import { useTranslationContext } from "@/context/TranslationContext";
import { change_password, edit_profile } from "@/constants/translation/auth.translation";

/**
 * @name ChangePassword
 * @description Component for changing user password.
 * 
 * API Response Format:
 * Success: { success: true, message: "backend_password_changed", data: { success: true } }
 * Error: { success: false, message: "backend_current_password_incorrect", data: {} }
 * 
 * @returns {JSX.Element} The ChangePassword component.
 */
const ChangePassword = () => {
  const t = useTranslationContext();
  const navigate = useNavigate();
  const checkLoggenInUser = useSelector(loggedUser);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Store display values for masked inputs
  const [currentPasswordDisplay, setCurrentPasswordDisplay] = useState('');
  const [newPasswordDisplay, setNewPasswordDisplay] = useState('');
  const [confirmPasswordDisplay, setConfirmPasswordDisplay] = useState('');
  
  // Track if user has interacted with any field
  const [hasInteracted, setHasInteracted] = useState(false);

  // Validation schema for change password form
  const validationSchema = useMemo(
    () => validationHelper.changePasswordValidation(t),
    [t]
  );

  // Formik setup
  const formik = useFormik({
    initialValues: {
      current_password: "",
      new_password: "",
      confirm_password: "",
    },
    validationSchema,
    onSubmit: (values) => {
      doChangePassword(values);
    },
  });

  // Clear any pre-filled values on component mount and prevent password manager
  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Clear all password inputs after component mounts (only on initial mount)
    const clearInputs = () => {
      const passwordInputs = document.querySelectorAll('input[type="password"]');
      passwordInputs.forEach(input => {
        input.value = '';
        input.setAttribute('value', '');
        input.defaultValue = '';
        // Set autocomplete to new-password to prevent password manager
        input.setAttribute('autocomplete', 'new-password');
      });
    };
    
    // Reset formik to empty values (only on initial mount)
    formik.resetForm();
    
    // Clear immediately 
    clearInputs();
    
    // Also clear on focus events for password fields
    const handleFocus = (e) => {
      if (e.target.type === 'password') {
        e.target.setAttribute('autocomplete', 'new-password');
      }
    };
    
    document.addEventListener('focus', handleFocus, true);
    
    return () => {
      document.removeEventListener('focus', handleFocus, true);
    };
  }, []); // Empty dependency array to run only on mount

  // Generate random field names to prevent Chrome detection
  const currentPasswordName = `pwd_${Math.random().toString(36).substr(2, 9)}`;
  const newPasswordName = `pwd_${Math.random().toString(36).substr(2, 9)}`;
  const confirmPasswordName = `pwd_${Math.random().toString(36).substr(2, 9)}`;

  // Handle cancel button click
  const handleCancel = () => {
    // Find and completely remove all password inputs from DOM
    const passwordInputs = document.querySelectorAll('input[type="password"]');
    const form = document.querySelector('.change-password-form');
    
    if (form && passwordInputs.length > 0) {
      // Create a temporary div to replace password inputs
      passwordInputs.forEach(input => {
        const tempDiv = document.createElement('div');
        tempDiv.style.height = input.offsetHeight + 'px';
        tempDiv.style.width = input.offsetWidth + 'px';
        tempDiv.style.display = 'inline-block';
        input.parentNode.replaceChild(tempDiv, input);
      });
      
      // Force Chrome to process the change
      setTimeout(() => {
        // Reset form completely
        formik.resetForm();
        // Navigate away
        navigate(dashboard);
      }, 50);
    } else {
      // Fallback - just reset and navigate
      formik.resetForm();
      navigate(dashboard);
    }
  };

  // Toggle password visibility
  const toggleCurrentPassword = () => setShowCurrentPassword(!showCurrentPassword);
  const toggleNewPassword = () => setShowNewPassword(!showNewPassword);
  const toggleConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

  // Change password mutation
  const { mutate: doChangePassword, isPending: isLoading } = useChangePassword(
    (response) => {
      console.log('ChangePassword success callback:', response);
      toast.success(response.message);
      formik.resetForm();
      // Redirect to dashboard after successful password change
      setTimeout(() => {
        navigate(dashboard);
      }, 1500);
    },
    (error) => {
      console.log('ChangePassword error callback:', error);
      const message = error?.response?.data?.message || error?.message || t(change_password.failed_to_change_error);
      toast.error(message);
    }
  );
  

  // Redirect to profile if not logged in
  useEffect(() => {
    if (!checkLoggenInUser || !checkLoggenInUser.isLoggedIn) {
      navigate(profile);
    }
  }, [checkLoggenInUser, navigate]);

  const tabs = [
    {
      label: t(edit_profile.main_heading),
      path: profile,
    },
    {
      label: t(change_password.main_heading),
      path: '/change-password',
    },
  ];

  return (
    <>
      {/* Hidden dummy form to distract Chrome password manager */}
      <form style={{ display: 'none' }} aria-hidden="true">
        <input type="text" name="username" />
        <input type="password" name="password" />
      </form>
      
      <CCardGroup>
        <CCard className="p-4 change-password-wrapper">
          <TabNavigation tabs={tabs} />
          <div className="text-center">
            <h2 className="fw-bold text-white">{t(change_password.main_heading)}</h2>
          </div>
          <CForm 
            className="change-password-form" 
            onSubmit={formik.handleSubmit} 
            autoComplete="off" 
            noValidate
            data-lpignore="true"
            data-form-type="other"
          >
            <CCardBody>
              {/* Current Password */}
              <CRow className="mb-3 justify-content-center">
                <CCol lg={6} md={8} sm={10} xs={12}>
                  <label className="form-label">{t(change_password.old_password_label)}</label>
                  <CInputGroup>
                    <CFormInput
                      type="text"
                      placeholder={t(change_password.old_password_placeholder)}
                      name={currentPasswordName}
                      autoComplete="off"
                      value={showCurrentPassword ? formik.values.current_password : currentPasswordDisplay}
                      onChange={(e) => {
                        setHasInteracted(true);
                        if (showCurrentPassword) {
                          formik.setFieldValue('current_password', e.target.value);
                        } else {
                          const newValue = e.target.value;
                          const actualValue = formik.values.current_password;
                          
                          // Simple logic: if new value is longer, add the last character
                          if (newValue.length > currentPasswordDisplay.length) {
                            const lastChar = newValue[newValue.length - 1];
                            formik.setFieldValue('current_password', actualValue + lastChar);
                          } else if (newValue.length < currentPasswordDisplay.length) {
                            // If shorter, remove characters
                            formik.setFieldValue('current_password', actualValue.slice(0, newValue.length));
                          }
                          
                          setCurrentPasswordDisplay(newValue.replace(/./g, '•'));
                        }
                      }}
                      onBlur={formik.handleBlur}
                      onFocus={(e) => {
                        e.target.setAttribute('autocomplete', 'off');
                        if (!showCurrentPassword) {
                          setCurrentPasswordDisplay(formik.values.current_password.replace(/./g, '•'));
                        }
                      }}
                      data-form-type="other"
                      data-lpignore="true"
                      spellCheck="false"
                      style={{ fontFamily: 'monospace' }}
                    />
                    <CInputGroupText onClick={toggleCurrentPassword}>
                      <FontAwesomeIcon icon={showCurrentPassword ? faEye : faEyeLowVision} />
                    </CInputGroupText>
                  </CInputGroup>
                  {hasInteracted && formik.errors.current_password && (
                    <div className="error text-danger mt-1">
                      {formik.errors.current_password}
                    </div>
                  )}
                </CCol>
              </CRow>

              {/* New Password */}
              <CRow className="mb-3 justify-content-center">
                <CCol lg={6} md={8} sm={10} xs={12}>
                  <label className="form-label">{t(change_password.new_password_label)}</label>
                  <CInputGroup>
                    <CFormInput
                      type="text"
                      placeholder={t(change_password.new_password_placeholder)}
                      name={newPasswordName}
                      autoComplete="off"
                      value={showNewPassword ? formik.values.new_password : newPasswordDisplay}
                      onChange={(e) => {
                        setHasInteracted(true);
                        if (showNewPassword) {
                          formik.setFieldValue('new_password', e.target.value);
                        } else {
                          const newValue = e.target.value;
                          const actualValue = formik.values.new_password;
                          
                          // Simple logic: if new value is longer, add the last character
                          if (newValue.length > newPasswordDisplay.length) {
                            const lastChar = newValue[newValue.length - 1];
                            formik.setFieldValue('new_password', actualValue + lastChar);
                          } else if (newValue.length < newPasswordDisplay.length) {
                            // If shorter, remove characters
                            formik.setFieldValue('new_password', actualValue.slice(0, newValue.length));
                          }
                          
                          setNewPasswordDisplay(newValue.replace(/./g, '•'));
                        }
                      }}
                      onBlur={formik.handleBlur}
                      onFocus={(e) => {
                        e.target.setAttribute('autocomplete', 'off');
                        if (!showNewPassword) {
                          setNewPasswordDisplay(formik.values.new_password.replace(/./g, '•'));
                        }
                      }}
                      data-form-type="other"
                      data-lpignore="true"
                      spellCheck="false"
                      style={{ fontFamily: 'monospace' }}
                    />
                    <CInputGroupText onClick={toggleNewPassword}>
                      <FontAwesomeIcon icon={showNewPassword ? faEye : faEyeLowVision} />
                    </CInputGroupText>
                  </CInputGroup>
                  {hasInteracted && formik.errors.new_password && (
                    <div className="error text-danger mt-1">
                      {formik.errors.new_password}
                    </div>
                  )}
                </CCol>
              </CRow>

              {/* Confirm Password */}
              <CRow className="mb-3 justify-content-center">
                <CCol lg={6} md={8} sm={10} xs={12}>
                  <label className="form-label">{t(change_password.confirm_password_label)}</label>
                  <CInputGroup>
                    <CFormInput
                      type="text"
                      placeholder={t(change_password.confirm_password_placeholder)}
                      name={confirmPasswordName}
                      autoComplete="off"
                      value={showConfirmPassword ? formik.values.confirm_password : confirmPasswordDisplay}
                      onChange={(e) => {
                        setHasInteracted(true);
                        if (showConfirmPassword) {
                          formik.setFieldValue('confirm_password', e.target.value);
                        } else {
                          const newValue = e.target.value;
                          const actualValue = formik.values.confirm_password;
                          
                          // Simple logic: if new value is longer, add the last character
                          if (newValue.length > confirmPasswordDisplay.length) {
                            const lastChar = newValue[newValue.length - 1];
                            formik.setFieldValue('confirm_password', actualValue + lastChar);
                          } else if (newValue.length < confirmPasswordDisplay.length) {
                            // If shorter, remove characters
                            formik.setFieldValue('confirm_password', actualValue.slice(0, newValue.length));
                          }
                          
                          setConfirmPasswordDisplay(newValue.replace(/./g, '•'));
                        }
                      }}
                      onBlur={formik.handleBlur}
                      onFocus={(e) => {
                        e.target.setAttribute('autocomplete', 'off');
                        if (!showConfirmPassword) {
                          setConfirmPasswordDisplay(formik.values.confirm_password.replace(/./g, '•'));
                        }
                      }}
                      data-form-type="other"
                      data-lpignore="true"
                      spellCheck="false"
                      style={{ fontFamily: 'monospace' }}
                    />
                    <CInputGroupText onClick={toggleConfirmPassword}>
                      <FontAwesomeIcon icon={showConfirmPassword ? faEye : faEyeLowVision} />
                    </CInputGroupText>
                  </CInputGroup>
                  {hasInteracted && formik.errors.confirm_password && (
                    <div className="error text-danger mt-1">
                      {formik.errors.confirm_password}
                    </div>
                  )}
                </CCol>
              </CRow>

              {/* Buttons */}
              <CRow>
                <CCol className="d-flex justify-content-center mt-2 gap-2">
                  <CButton
                    color="primary"
                    className="px-4"
                    type="submit"
                    disabled={isLoading}
                  >
                    {isLoading && <CSpinner size="sm" variant="grow" />}
                    {t(change_password.submit_btn_text)}
                  </CButton>
                  <CButton
                    color="secondary"
                    className="px-4"
                    type="button"
                    disabled={isLoading}
                    onClick={handleCancel}
                  >
                    {t(change_password.cancel_button)}
                  </CButton>
                </CCol>
              </CRow>
            </CCardBody>
          </CForm>
        </CCard>
      </CCardGroup>
    </>
  );
};

export default ChangePassword;
